import {useLayoutEffect} from 'react';
import {languageFromPath, setSiteLanguage, t, useSiteLanguage} from './i18n';
import {lazy, PropsWithChildren, Suspense, useDeferredValue, useEffect} from 'react';
import {BrowserRouter, Navigate, Route, Routes, useLocation} from 'react-router';
import ScrollToTopAuto from './commonComponent/scrollToTopOfPage/ScrollToTopAuto';
import HomePage from './component/homePage/HomePage';
import {pageLoaders, preloadPage} from './utils/pageLoaders';
import {useScrollReveal} from './hooks/useScrollReveal';
import {usePageMeta} from './hooks/usePageMeta';
import './styles/index.css';
import './styles/animations.css';
import './styles/design.css';
import PageLoadBoundary from './commonComponent/loader/PageLoadBoundary';

const NotFoundPage = lazy(() => import('./component/notFoundPage/NotFoundPage'));
const CardMenuPage = lazy(pageLoaders['/carte']);
const ImageGalleryPage = lazy(pageLoaders['/galerie']);
const PoliticConfidentiality = lazy(pageLoaders['/politique-confidentialite']);
const MentionsLegales = lazy(pageLoaders['/mentions-legales']);

function Page({children}: PropsWithChildren) {
    useSiteLanguage();
    const {pathname} = useLocation();
    const ref = useScrollReveal(pathname);
    // Posé ici et non dans chaque page : `Page` enveloppe toutes les routes, donc une route
    // ajoutée demain reçoit ses métadonnées sans qu'on y pense — et une route absente de
    // `seo.json` sort de l'index au lieu d'hériter du titre de l'accueil.
    usePageMeta(pathname);
    return <div className="page-enter" ref={ref}>
        <ScrollToTopAuto />
        {children}
    </div>;
}

function SiteRoutes() {
    useSiteLanguage();
    const location = useLocation();
    useSiteLanguage();
    useLayoutEffect(() => setSiteLanguage(languageFromPath(location.pathname)), [location.pathname]);
    // Keep the current page usable while the destination's bundle arrives.
    const deferredLocation = useDeferredValue(location);
    return <Suspense fallback={<div className="route-loading" role="status" aria-label={t("common.pageLoading")}><span /></div>}>
        <Routes location={deferredLocation}>
            <Route path="/" element={<Page key="home"><HomePage /></Page>} />
            <Route path="/en" element={<Page key="home"><HomePage /></Page>} />
            <Route path="/carte" element={<Page key="carte"><CardMenuPage /></Page>} />
            <Route path="/en/menu" element={<Page key="carte"><CardMenuPage /></Page>} />
            <Route path="/galerie" element={<Page key="galerie"><ImageGalleryPage /></Page>} />
            <Route path="/en/gallery" element={<Page key="galerie"><ImageGalleryPage /></Page>} />
            <Route path="/politique-confidentialite" element={<Page key="confidentialite"><PoliticConfidentiality /></Page>} />
            <Route path="/en/privacy" element={<Page key="confidentialite"><PoliticConfidentiality /></Page>} />
            <Route path="/mentions-legales" element={<Page key="mentions"><MentionsLegales /></Page>} />
            <Route path="/en/legal" element={<Page key="mentions"><MentionsLegales /></Page>} />
            {/* La politique de cookies a été supprimée — le site ne dépose aucun cookie. Une
                redirection permanente vit dans `vercel.json` et c'est elle qui parle aux
                moteurs ; celle-ci est le filet, pour le cas où la plateforme ne l'appliquerait
                pas. Voir `docs/redirections.md`. */}
            <Route path="/politique-cookie" element={<Navigate to="/politique-confidentialite" replace />} />
            <Route path="*" element={<Page key="404"><NotFoundPage /></Page>} />
        </Routes>
    </Suspense>;
}

export default function App() {
    useSiteLanguage();
    useEffect(() => {
        document.addEventListener('pointerover', preloadPage, {passive: true});
        document.addEventListener('focusin', preloadPage);
        return () => {
            document.removeEventListener('pointerover', preloadPage);
            document.removeEventListener('focusin', preloadPage);
        };
    }, []);

    return <BrowserRouter><PageLoadBoundary><SiteRoutes /></PageLoadBoundary></BrowserRouter>;
}
